import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { CONTACT } from 'config/contact';
import type { DigitalMarketingService } from './services';

interface DigitalMarketingServicePageProps {
  service: DigitalMarketingService;
}

const DigitalMarketingServicePage = ({ service }: DigitalMarketingServicePageProps) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const canonical = `https://www.makemindautomations.tech${service.route}`;

  const engagementModels = [
    {
      name: 'One-Time Setup',
      description: 'Audit, setup, and launch with documentation your team can run with.',
      points: ['Full account and tracking audit', 'Campaign or profile setup', 'Handover notes and training'],
    },
    {
      name: 'Monthly Retainer',
      description: 'Ongoing management, content, and optimization with monthly reporting.',
      points: ['Dedicated manager', 'Weekly optimization', 'Monthly performance report'],
    },
    {
      name: 'Growth Partner',
      description: 'Marketing and automation together, with quarterly strategy reviews.',
      points: ['Combined marketing + automation', 'Funnel and CRM integration', 'Quarterly strategy sessions'],
    },
  ];

  return (
    <>
      <Helmet>
        <title>{service.seoTitle}</title>
        <meta name="description" content={service.seoDescription} />
        <meta name="keywords" content={service.keywords} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={service.seoTitle} />
        <meta property="og:description" content={service.seoDescription} />
        <meta property="og:url" content={canonical} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={service.seoTitle} />
        <meta name="twitter:description" content={service.seoDescription} />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <main>
          {/* Hero */}
          <section className="py-20 lg:py-28 bg-background">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-3xl">
                <div className="inline-flex items-center space-x-2 px-3 py-1 bg-accent/10 border border-accent/20 rounded-full mb-6">
                  <Icon name={service.icon} size={14} className="text-accent" />
                  <span className="text-xs font-semibold text-accent uppercase tracking-wider">Digital Marketing</span>
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-text-primary tracking-tight mb-6">
                  {service.title}
                </h1>
                <p className="text-xl text-primary font-semibold mb-4">{service.tagline}</p>
                <p className="text-lg text-text-secondary leading-relaxed mb-10">{service.longDescription}</p>
                <div className="flex flex-wrap gap-4">
                  <Link to="/contact">
                    <Button variant="default" size="lg" iconName="Calendar" iconPosition="left">
                      Book Free Audit
                    </Button>
                  </Link>
                  <a href={CONTACT.whatsappLink} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="lg" iconName="MessageCircle" iconPosition="left">
                      Chat on WhatsApp
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Deliverables */}
          <section className="py-20 bg-surface">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-4">What is included</h2>
              <p className="text-lg text-text-secondary max-w-3xl mb-12">
                Everything below is part of the service. No hidden extras, no jargon-filled reports.
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {service.deliverables.map((item) => (
                  <div key={item} className="flex items-start space-x-3 bg-card border border-border rounded-2xl p-6">
                    <Icon name="Check" size={20} className="text-success mt-0.5 flex-shrink-0" />
                    <span className="text-text-primary font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Process */}
          <section className="py-20 bg-background">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-12">How we work</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {service.process.map((step, index) => (
                  <div key={step.title} className="bg-card border border-border rounded-2xl p-6">
                    <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                      <span className="text-primary font-bold">{index + 1}</span>
                    </div>
                    <h3 className="text-lg font-bold text-text-primary mb-2">{step.title}</h3>
                    <p className="text-sm text-text-secondary leading-relaxed">{step.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Tools */}
          <section className="py-20 bg-surface">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-8">Tools we work with</h2>
              <div className="flex flex-wrap gap-3">
                {service.tools.map((tool) => (
                  <span
                    key={tool}
                    className="bg-background border border-border px-4 py-2 rounded-lg text-sm font-medium text-text-primary"
                  >
                    {tool}
                  </span>
                ))}
              </div>
            </div>
          </section>

          {/* Engagement models */}
          <section className="py-20 bg-background">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-4">Ways to work with us</h2>
              <p className="text-lg text-text-secondary max-w-3xl mb-12">
                Pricing is quoted after a free audit, based on your market and goals. No fixed packages that do not fit.
              </p>
              <div className="grid md:grid-cols-3 gap-8">
                {engagementModels.map((model) => (
                  <div key={model.name} className="bg-card border border-border rounded-2xl p-8">
                    <h3 className="text-xl font-bold text-text-primary mb-2">{model.name}</h3>
                    <p className="text-sm text-text-secondary leading-relaxed mb-6">{model.description}</p>
                    <ul className="space-y-2.5">
                      {model.points.map((point) => (
                        <li key={point} className="flex items-start space-x-2.5 text-sm text-text-secondary">
                          <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-20 bg-surface">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-12 text-center">
                Frequently Asked Questions
              </h2>
              <div className="space-y-6">
                {service.faqs.map((faq) => (
                  <div key={faq.question} className="bg-card border border-border rounded-2xl p-6">
                    <h3 className="text-lg font-bold text-text-primary mb-2">{faq.question}</h3>
                    <p className="text-text-secondary leading-relaxed">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary/10 via-background to-accent/10">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-4">
                Ready to grow with {service.title}?
              </h2>
              <p className="text-lg text-text-secondary mb-8 max-w-2xl mx-auto">
                Book a free audit and we will show you exactly what is working, what is leaking, and what to do first.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <Link to="/contact">
                  <Button variant="default" size="lg" iconName="Calendar" iconPosition="left">
                    Start Your Free Audit
                  </Button>
                </Link>
                <a href={CONTACT.whatsappLink} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="lg" iconName="MessageCircle" iconPosition="left">
                    Chat on WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default DigitalMarketingServicePage;
